import { Card, Box, Typography, Stack, Tooltip } from '@mui/material';
import { colors } from '../../../ui/colors';
import { Project } from '../types/dashboard.types';
import { formatTimeAgo } from '../helpers/time-ago.helper';
import { StatusChip } from './StatusChip';
import { ProjectCardMenu } from './ProjectCardMenu';

interface ProjectCardProps {
  project: Project;
  onOpen: (projectId: string, projectType: string) => void;
  onRename: (projectId: string, projectName: string) => void;
  onDuplicate: (projectId: string) => void;
  onDelete: (projectId: string, projectName: string) => void;
}

const getGradientFromId = (id: string): string => {
  const gradients = [
    'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
    'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)',
    'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)',
    'linear-gradient(135deg, #43e97b 0%, #38f9d7 100%)',
    'linear-gradient(135deg, #fa709a 0%, #fee140 100%)',
    'linear-gradient(135deg, #30cfd0 0%, #330867 100%)',
  ];
  const hash = id.split('').reduce((acc, char) => acc + char.charCodeAt(0), 0);
  return gradients[hash % gradients.length];
};

export const ProjectCard: React.FC<ProjectCardProps> = ({
  project,
  onOpen,
  onRename,
  onDuplicate,
  onDelete,
}) => {
  const gradient = getGradientFromId(project.id);


  const imgSrc = project.thumbnailUrl
    ? `${project.thumbnailUrl}?v=${encodeURIComponent(project.updatedAt)}`
    : '';


  return (
    <Card
      onClick={() => onOpen(project.id, project.status)}
      sx={{
        borderRadius: 3,
        overflow: 'hidden',
        cursor: 'pointer',
        backgroundColor: 'var(--fx-surface-base, #FFFFFF)',
        height: '100%',
        minHeight: 360,
        display: 'flex',
        flexDirection: 'column',
        transition: 'transform 180ms ease, box-shadow 180ms ease',
        boxShadow: '0 6px 18px rgba(0,0,0,0.08)',
        '&:hover': {
          boxShadow: '0 14px 34px rgba(0,0,0,0.16)',
          transform: 'translateY(-4px)',
        },
      }}
    >
      {/* Thumbnail (con fallback + overlay) */}
      <Box sx={{ position: 'relative' }}>
        <Box
          component="img"
          key={`${project.id}-${project.updatedAt}`}
          src={imgSrc}
          alt={project.projectName}
          onError={(e: any) => {
            e.currentTarget.style.display = 'none';
          }}
          sx={{
            height: 210,
            width: '100%',
            display: 'block',
            objectFit: 'cover',
            backgroundColor: 'var(--fx-surface-muted, #F1F5F9)',
          }}
        />

        {!project.thumbnailUrl && (
          <Box
            sx={{
              height: 210,
              width: '100%',
              background: gradient,
              display: 'flex',
              alignItems: 'flex-end',
              p: 2,
            }}
          >
            <Typography
              sx={{
                color: '#fff',
                fontWeight: 700,
                fontSize: 18,
                lineHeight: 1.1,
                textShadow: '0 6px 18px rgba(0,0,0,0.35)',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
                width: '100%',
              }}
            >
              {project.projectName}
            </Typography>
          </Box>
        )}

        <Box
          sx={{
            pointerEvents: 'none',
            position: 'absolute',
            inset: 0,
            background:
              'linear-gradient(to bottom, rgba(0,0,0,0.00) 35%, rgba(0,0,0,0.28) 100%)',
          }}
        />
      </Box>

      {/* Content */}
      <Box
        sx={{
          px: 2,
          pt: 1.75,
          pb: 2,
          flex: 1,
          display: 'flex',
          flexDirection: 'column',
          gap: 1.25,
        }}
      >
        {/* Header */}
        <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 1 }}>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Tooltip title={project.projectName}>
              <Typography
                variant="subtitle1"
                sx={{
                  fontWeight: 700,
                  color: 'var(--fx-surface-text, #0F172A)',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                }}
              >
                {project.projectName}
              </Typography>
            </Tooltip>

            <Stack direction="row" spacing={1} alignItems="center" sx={{ mt: 0.75 }}>
              <StatusChip status={project.status} size="small" />
              <Typography
                variant="caption"
                sx={{ color: 'var(--fx-surface-texttertiary, #94A3B8)', whiteSpace: 'nowrap' }}
              >
                {formatTimeAgo(project.updatedAt)}
              </Typography>
            </Stack>
          </Box>

          <Box onClick={(e) => e.stopPropagation()} sx={{ mt: 0.25 }}>
            <ProjectCardMenu
              projectStatus={project.status}
              projectId={project.id}
              projectName={project.projectName}
              onOpen={onOpen}
              onRename={onRename}
              onDuplicate={onDuplicate}
              onDelete={onDelete}
            />
          </Box>
        </Box>

        {/* Slug */}
        <Tooltip title={project.slug}>
          <Typography
            variant="body2"
            sx={{
              color: 'var(--fx-surface-textsecondary, #64748B)',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
          >
            {project.slug}
          </Typography>
        </Tooltip>

        {/* Footer */}
        <Box
          sx={{
            mt: 'auto',
            pt: 1.25,
            borderTop: '1px solid var(--fx-surface-border, #E2E8F0)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 1,
          }}
        >
          <Tooltip title={`Template: ${project.templateId}`}>
            <Typography
              variant="caption"
              sx={{
                color: colors.accent || '#ff9500',
                fontWeight: 600,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
                maxWidth: '70%',
              }}
            >
              {project.templateId}
            </Typography>
          </Tooltip>

          <Typography variant="caption" sx={{ color: 'var(--fx-surface-texttertiary, #94A3B8)', whiteSpace: 'nowrap' }}>
            ID: {project.id.slice(0, 6)}
          </Typography>
        </Box>
      </Box>
    </Card>
  );
};
